import type {
  MarkdownAstNode,
  ParsedMarkdownAst,
} from '@/lib/markdown/parse-markdown-ast'

export const DEFAULT_WORD_EXPORT_FILE_NAME = 'converted-document.docx'

const MAX_WORD_FILE_NAME_CODE_POINTS = 100
const INVALID_FILE_NAME_CHARACTERS =
  /[\u0000-\u001F\u007F<>:"/\\|?*\u202A-\u202E\u2066-\u2069]/g
const WINDOWS_RESERVED_FILE_NAME =
  /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i

const getHeadingText = (node: MarkdownAstNode): string => {
  if (node.type === 'image' || node.type === 'html') return ''
  if (typeof node.value === 'string') return node.value
  if (!Array.isArray(node.children)) return ''
  return node.children.map((child) => getHeadingText(child)).join('')
}

const findFirstH1 = (
  nodes: MarkdownAstNode[] | undefined,
): MarkdownAstNode | null => {
  if (!nodes) return null

  for (const node of nodes) {
    if (node.type === 'heading' && node.depth === 1) return node

    const nestedHeading = findFirstH1(node.children)
    if (nestedHeading) return nestedHeading
  }

  return null
}

const sanitizeWordFileNameBase = (value: string) => {
  const normalized = value
    .normalize('NFC')
    .replace(INVALID_FILE_NAME_CHARACTERS, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\.docx$/i, '')
    .replace(/[. ]+$/g, '')

  const truncated = Array.from(normalized)
    .slice(0, MAX_WORD_FILE_NAME_CODE_POINTS)
    .join('')
    .replace(/[. ]+$/g, '')

  if (!truncated || truncated === '.' || truncated === '..') return ''
  return WINDOWS_RESERVED_FILE_NAME.test(truncated)
    ? `_${truncated}`
    : truncated
}

export const getWordExportFileName = (parsedMarkdown: ParsedMarkdownAst) => {
  const firstH1 = findFirstH1(parsedMarkdown.root.children)
  if (!firstH1) return DEFAULT_WORD_EXPORT_FILE_NAME

  const safeTitle = sanitizeWordFileNameBase(getHeadingText(firstH1))
  return safeTitle ? `${safeTitle}.docx` : DEFAULT_WORD_EXPORT_FILE_NAME
}
